import React from 'react';
import {message} from 'antd';
import {workspaceApi} from '@/api/projects';
import type {WorkspaceSearchHit} from '@/types/api';

const DEBOUNCE_MS = 250;
const PAGE_SIZE = 20;

export function useWorkspaceSearch() {
	const [query, setQuery] = React.useState('');
	const [hits, setHits] = React.useState<WorkspaceSearchHit[]>([]);
	const [hasMore, setHasMore] = React.useState(false);
	const [loading, setLoading] = React.useState(false);
	const [loadingMore, setLoadingMore] = React.useState(false);
	const [open, setOpen] = React.useState(false);
	const requestId = React.useRef(0);
	const loadingMoreRef = React.useRef(false);

	React.useEffect(() => {
		const q = query.trim();
		if (!q) {
			setHits([]);
			setHasMore(false);
			setLoading(false);
			setLoadingMore(false);
			return;
		}
		setHits([]);
		setHasMore(false);
		setLoading(true);
		const id = ++requestId.current;
		const timer = window.setTimeout(() => {
			void workspaceApi.search(q, 0, PAGE_SIZE).then(({data, error}) => {
				if (id !== requestId.current) return;
				setLoading(false);
				if (error) {
					message.error(error.message || 'Не удалось выполнить поиск');
					setHits([]);
					setHasMore(false);
					return;
				}
				setHits(data?.items ?? []);
				setHasMore(Boolean(data?.hasMore));
			});
		}, DEBOUNCE_MS);
		return () => window.clearTimeout(timer);
	}, [query]);

	const loadMore = React.useCallback(() => {
		const q = query.trim();
		if (!q || !hasMore || loading || loadingMoreRef.current) return;
		loadingMoreRef.current = true;
		setLoadingMore(true);
		const id = requestId.current;
		const offset = hits.length;
		void workspaceApi.search(q, offset, PAGE_SIZE).then(({data, error}) => {
			loadingMoreRef.current = false;
			if (id !== requestId.current) return;
			setLoadingMore(false);
			if (error) {
				message.error(error.message || 'Не удалось загрузить ещё результаты');
				return;
			}
			const next = data?.items ?? [];
			setHits(prev => [...prev, ...next]);
			setHasMore(Boolean(data?.hasMore) && next.length > 0);
		});
	}, [query, hasMore, loading, hits.length]);

	const clear = React.useCallback(() => {
		requestId.current += 1;
		loadingMoreRef.current = false;
		setQuery('');
		setHits([]);
		setHasMore(false);
		setLoading(false);
		setLoadingMore(false);
		setOpen(false);
	}, []);

	return {query, setQuery, hits, hasMore, loading, loadingMore, loadMore, open, setOpen, clear};
}
